/**
 * API endpoint path constants
 *
 * Centralized definition of all API endpoints to ensure
 * consistency between frontend and backend.
 */

/**
 * Encodes a single dynamic path segment for safe URL interpolation.
 *
 * Uses `encodeURIComponent` to escape characters that would break URL
 * routing or be exploitable (e.g. `/`, `?`, `#`, spaces, `%`).
 * Apply this to every dynamic value interpolated into a URL path —
 * but NOT to static path separators or query strings.
 */
export function encodePathSegment(segment: string): string {
  return encodeURIComponent(segment);
}

export const ENDPOINTS = {
  /** Health check endpoint */
  health: "/health",

  /** Authentication endpoints */
  auth: {
    /** Get current session */
    session: "/api/auth/get-session",
    /** Sign in with Microsoft */
    signIn: "/api/auth/sign-in/microsoft",
    /** Sign out current user */
    signOut: "/api/auth/sign-out",
  },

  /** Pain entries endpoint */
  painEntries: "/api/pain-entries",

  /** Pain locations endpoint */
  PAIN_LOCATION: "/api/pain-locations",

  /** Daily records endpoints */
  DAILY_RECORDS: "/api/daily-records",
  DAILY_RECORD_BY_DATE: (date: string) => `/api/daily-records/${encodePathSegment(date)}`,

  /** Dashboard endpoint */
  DASHBOARD: "/api/dashboard",

  /** User settings endpoint */
  USER_SETTINGS: "/api/user-settings",

  /** User profile endpoint */
  USER_PROFILE: "/api/user-profile",

  /** Events endpoints */
  EVENTS: "/api/events",
  EVENT_BY_ID: (id: string) => `/api/events/${encodePathSegment(id)}`,
  EVENT_CORRELATION: (id: string) => `/api/events/${encodePathSegment(id)}/correlation`,
  /** Active medications from events endpoint */
  ACTIVE_MEDICATIONS: "/api/events/active-medications",

  /** Audio uploads endpoint */
  AUDIO_UPLOADS: "/api/audio-uploads",
} as const;

/** Transcription endpoints */
export const TRANSCRIPTIONS = {
  /** POST - Create a transcription from audio file */
  CREATE: "/api/transcriptions",
} as const;

/** Extraction endpoints */
export const EXTRACTION = {
  /** POST - Extract pain data from transcript */
  EXTRACT: "/api/extract",
} as const;

/** Insights endpoints */
export const INSIGHTS = {
  /** GET - Generate AI-powered insights from pain data */
  GET: "/api/insights",
  /** GET - List dismissed insight IDs */
  LIST_DISMISSED: "/api/insights/dismissed",
  /** POST - Dismiss an insight */
  DISMISS: "/api/insights/dismissed",
  /** DELETE - Restore a dismissed insight (append insight ID) */
  RESTORE_DISMISSED: (insightId: string) =>
    `/api/insights/dismissed/${encodePathSegment(insightId)}`,
  /** DELETE - Clear all dismissed insights */
  CLEAR_DISMISSED: "/api/insights/dismissed",
} as const;

/** Export endpoints */
export const EXPORTS = {
  /** POST - Generate export data */
  GENERATE: '/api/exports',
  /** POST - Generate machine-readable JSON export */
  JSON: '/api/exports/json',
  /** GET - Generate text summary */
  TEXT_SUMMARY: '/api/exports/text-summary',
} as const;

/** Medication endpoints */
export const MEDICATIONS = {
  /** GET - Medication name suggestions from user history */
  SUGGESTIONS: '/api/medications/suggestions',
  /** GET - List user medications with status filter */
  LIST: '/api/medications',
  /** POST - Create a new user medication */
  CREATE: '/api/medications',
  /** PATCH - Update a user medication by ID */
  BY_ID: (id: string) => `/api/medications/${encodePathSegment(id)}`,
  /** POST - Switch from one medication to another */
  SWITCH: (id: string) => `/api/medications/${encodePathSegment(id)}/switch`,
} as const;

/** Location endpoints */
export const LOCATIONS = {
  /** GET - List user locations */
  LIST: '/api/locations',
  /** GET - Search valid cities */
  SEARCH: '/api/locations/search',
  /** POST - Create a location from a city query */
  CREATE: '/api/locations',
  /** PATCH - Update a location (rename, set default) */
  BY_ID: (id: string) => `/api/locations/${encodePathSegment(id)}`,
} as const;

/** Weather endpoints */
export const WEATHER = {
  /** GET - Get weather data for a date range */
  GET: '/api/weather',
} as const;

/** Doctor Visit endpoints */
export const DOCTOR_VISITS = {
  /** GET - List doctor visits / POST - Create a doctor visit */
  LIST: '/api/doctor-visits',
  CREATE: '/api/doctor-visits',
  /** GET - Get / PATCH - Update a doctor visit by ID */
  BY_ID: (id: string) => `/api/doctor-visits/${encodePathSegment(id)}`,
  /** GET - List items / POST - Create items for a visit */
  ITEMS: (visitId: string) => `/api/doctor-visits/${encodePathSegment(visitId)}/items`,
  /** PATCH - Update a visit item */
  ITEM_BY_ID: (visitId: string, itemId: string) => `/api/doctor-visits/${encodePathSegment(visitId)}/items/${encodePathSegment(itemId)}`,
  /** GET - Suggested timeframe for visit prep */
  SUGGESTED_TIMEFRAME: '/api/doctor-visits/suggested-timeframe',
  /** GET - Visit prep context (aggregated data) */
  CONTEXT: (visitId: string) => `/api/doctor-visits/${encodePathSegment(visitId)}/context`,
  /** POST - Export visit packet (PDF/text) */
  EXPORT: (visitId: string) => `/api/doctor-visits/${encodePathSegment(visitId)}/export`,
} as const;

/** Consent management endpoints (GDPR Art. 9/13) */
export const CONSENTS = {
  /** GET - Current consent state + policy version */
  GET: '/api/consents',
  /** PUT - Update consent decisions */
  UPDATE: '/api/consents',
  /** GET - Append-only audit trail */
  HISTORY: '/api/consents/history',
} as const;

/** Agent endpoints for AG-UI streaming */
export const AGENT = {
  /** POST - Stream AG-UI events for check-in agent */
  CHECK_IN: '/api/agent/check-in',
  /** GET - Get agent info */
  CHECK_IN_INFO: '/api/agent/check-in/info',
  /** POST - Confirm changes (HITL flow) */
  CHECK_IN_CONFIRM: '/api/agent/check-in/confirm',
  /** POST - Detect conflicts before saving (pre-flight check) */
  CHECK_IN_DETECT_CONFLICTS: '/api/agent/check-in/detect-conflicts',
} as const;
